import type { Commercial, Contract, DocumentKind, Renewal } from "./api";

export const COMMERCIAL_TEXT: Record<Commercial, string> = { active: "Active", hold: "On hold", inactive: "Inactive" };
export const COMMERCIALS: Commercial[] = ["active", "hold", "inactive"];
export const KIND_TEXT: Record<DocumentKind, string> = {
  agreement: "Signed agreement",
  cancellation: "Cancellation letter",
  other: "Other",
};
export const KINDS: DocumentKind[] = ["agreement", "cancellation", "other"];
/** MAX_DOCUMENT_BYTES in app/contracts.py. */
export const MAX_BYTES = 10_000_000;

export type Window = "all" | "ended" | number;

/** "Ends in 5 days", "Ends today" or "Ended 3 days ago". */
export function leftText(days: number): string {
  if (days === 0) return "Ends today";
  if (days > 0) return days === 1 ? "Ends tomorrow" : `Ends in ${days} days`;
  return days === -1 ? "Ended yesterday" : `Ended ${-days} days ago`;
}

/** Spec F-SAL-8: a window holds the contracts that end in that many days or fewer, not the ended ones. */
export function inWindow(renewal: Renewal, window: Window): boolean {
  if (window === "all") return true;
  if (window === "ended") return renewal.days_left < 0;
  return renewal.days_left >= 0 && renewal.days_left <= window;
}

export function windowCounts(renewals: Renewal[], windows: number[]): [Window, number][] {
  const all: Window[] = ["all", "ended", ...windows];
  return all.map((window) => [window, renewals.filter((r) => inWindow(r, window)).length]);
}

export const windowText = (window: Window) => (window === "all" ? "All" : window === "ended" ? "Ended" : `${window} days`);

export function readWindow(search: string, windows: number[]): Window {
  const asked = new URLSearchParams(search).get("window");
  if (asked === "ended") return "ended";
  const days = Number(asked);
  return windows.includes(days) ? days : "all";
}

/** The problem with a file before the upload, or null. The API checks the file again. */
export function fileProblem(file: { name: string; size: number } | null): string | null {
  if (!file) return "Choose a PDF file.";
  if (!file.name.toLowerCase().endsWith(".pdf")) return "Only a PDF file is accepted.";
  if (file.size < 1) return "The file is empty.";
  if (file.size > MAX_BYTES) return `A file can have ${MAX_BYTES / 1_000_000} MB at most.`;
  if (file.name.length > 200) return "The file name can have 200 characters at most.";
  return null;
}

export async function sha256Hex(data: ArrayBuffer): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", data);
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("");
}

export function sizeText(bytes: number): string {
  if (bytes < 1000) return `${bytes} B`;
  if (bytes < 1_000_000) return `${Math.round(bytes / 1000)} kB`;
  return `${(bytes / 1_000_000).toFixed(1)} MB`;
}

export const ratingText = (rating: number | null) => (rating === null ? "–" : `${rating} of 5`);

/** The text limits of CheckInEntry and ContractChange in app/main.py. */
export const LIMITS = { contact: 200, result: 1000, payee: 200, payeeAddress: 300, consideration: 300 } as const;

const tooLong = (label: string, text: string, most: number) =>
  text.trim().length > most ? `${label} can have ${most} characters at most.` : null;

/** The problem with a check-in, or null. Rule Q-21; the API checks the same rules. */
export function checkInProblem(entry: {
  metOn: string;
  nextOn: string;
  rating: number;
  today: string;
  contact: string;
  result: string;
}): string | null {
  if (!entry.metOn) return "Enter the day of the check-in.";
  if (entry.metOn > entry.today) return "A check-in cannot be in the future.";
  if (!entry.contact.trim()) return "Enter the contact of the check-in.";
  const long = tooLong("The contact", entry.contact, LIMITS.contact);
  if (long) return long;
  if (!(entry.rating >= 1 && entry.rating <= 5)) return "Choose a rating from 1 to 5.";
  if (!entry.result.trim()) return "Enter the result of the check-in.";
  const longResult = tooLong("The result", entry.result, LIMITS.result);
  if (longResult) return longResult;
  if (!entry.nextOn) return "Enter the day of the next check-in.";
  if (entry.nextOn <= entry.metOn) return "The next check-in must be after this one.";
  return null;
}

/** The problem with the text and the notice of a terms change, or null. */
export type TermsField = "notice" | "amount" | "consideration" | "payee" | "payeeAddress";

/** The first problem of the terms form, with the field that has it. */
export function termsProblem(terms: { payee: string; payeeAddress: string; consideration: string; consideration_amount: string; notice: string }): { field: TermsField; text: string } | null {
  const notice = Number(terms.notice);
  if (terms.notice.trim() === "" || !Number.isInteger(notice) || notice < 0 || notice > 365) return { field: "notice", text: "Enter the notice as a whole number of days from 0 to 365." };
  const amount = terms.consideration_amount.trim() === "" ? 0 : Number(terms.consideration_amount);
  if (!Number.isFinite(amount) || amount < 0) return { field: "amount", text: "Enter an added consideration amount of 0 or more." };
  const long: [TermsField, string, string, number][] = [
    ["consideration", "The added consideration", terms.consideration, LIMITS.consideration],
    ["payee", "The payee", terms.payee, LIMITS.payee],
    ["payeeAddress", "The payee mail address", terms.payeeAddress, LIMITS.payeeAddress],
  ];
  for (const [field, name, text, limit] of long) {
    const problem = tooLong(name, text, limit);
    if (problem) return { field, text: problem };
  }
  return null;
}

/** The first day of the version after this one, or null when this version is the newest. */
export function replacedOn(versions: readonly Contract[], version: Contract): string | null {
  const later = versions.filter((v) => v.version > version.version).sort((a, b) => a.version - b.version);
  return later[0]?.starts_on ?? null;
}
